<template>
  <main class="all-news-page">
    <section class="all-news-section" aria-labelledby="all-news-title">
      <div class="all-news-container">
        <div class="all-news-toolbar">
          <h1 id="all-news-title" class="all-news-breadcrumb">
            <router-link to="/news">{{ t('news.newsroom') }}</router-link>
            <span aria-hidden="true">›</span>
            <strong>{{ t('news.all') }}</strong>
          </h1>

          <label class="category-filter">
            <span class="sr-only">{{ t('news.filterLabel') }}</span>
            <select v-model="activeCategoryId" :disabled="loading" @change="selectCategory">
              <option value="">{{ t('news.allCategories') }}</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </label>
        </div>

        <div v-if="loading" class="all-news-state" role="status" aria-live="polite">
          <span class="all-news-spinner" aria-hidden="true"></span>
          <p>{{ t('news.loading') }}</p>
        </div>

        <div v-else-if="error" class="all-news-state all-news-state--message">
          <EmptyState
            icon="error"
            :title="t('news.loadingFailed')"
            :description="error"
            :show-action="true"
            :action-text="t('news.reload')"
            @action="refreshNews"
          />
        </div>

        <template v-else-if="articles.length">
          <div class="all-news-list">
            <NewsListCard v-for="item in articles" :key="item.id" :item="item" />
          </div>

          <nav class="news-pagination" :aria-label="t('news.pagination')">
            <button
              class="pagination-arrow pagination-arrow--previous"
              type="button"
              :disabled="currentPage === 1"
              :aria-label="t('news.previousPage')"
              @click="changePage(currentPage - 1)"
            >
              <img src="/static/images/newsroom-v2/pagination-next.svg" alt="" aria-hidden="true">
            </button>

            <template v-for="item in paginationItems" :key="item">
              <span v-if="typeof item === 'string'" class="pagination-ellipsis" aria-hidden="true">…</span>
              <button
                v-else
                class="pagination-page"
                :class="{ 'is-active': item === currentPage }"
                type="button"
                :aria-label="`${t('news.goToPage')} ${item}`"
                :aria-current="item === currentPage ? 'page' : undefined"
                @click="changePage(item)"
              >
                {{ item }}
              </button>
            </template>

            <button
              class="pagination-arrow"
              type="button"
              :disabled="currentPage === totalPages"
              :aria-label="t('news.nextPage')"
              @click="changePage(currentPage + 1)"
            >
              <img src="/static/images/newsroom-v2/pagination-next.svg" alt="" aria-hidden="true">
            </button>
          </nav>
        </template>

        <div v-else class="all-news-state all-news-state--message">
          <EmptyState
            icon="news"
            :title="t('news.noContent')"
            :description="t('news.noArticlesInCategory')"
            :show-action="false"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import EmptyState from '@/components/EmptyState.vue'
import NewsListCard from '@/components/NewsListCard.vue'
import { newsAPI } from '@/utils/api'

const ALL_NEWS_PAGE_SIZE = 8

const {
  getPaginationItems,
  getTotalPages,
  normalizeNewsCategoriesResponse,
  normalizeNewsPageResponse
} = require('@/utils/news-list.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

export default {
  name: 'AllNews',
  components: {
    EmptyState,
    NewsListCard
  },
  mixins: [siteLanguageMixin],
  data() {
    return {
      articles: [],
      categories: [],
      activeCategoryId: '',
      currentPage: 1,
      totalCount: 0,
      loading: false,
      error: null,
      categoriesLoaded: false,
      newsRequestGuard: createLatestSiteRequestGuard(),
      categoryRequestGuard: createLatestSiteRequestGuard()
    }
  },
  computed: {
    pageSize() {
      return ALL_NEWS_PAGE_SIZE
    },
    totalPages() {
      return getTotalPages(this.totalCount, this.pageSize)
    },
    paginationItems() {
      return getPaginationItems(this.currentPage, this.totalPages)
    }
  },
  async mounted() {
    document.title = 'All News - ANOVATOR'
    await this.fetchNews(1)
    await this.fetchCategories()
  },
  watch: {
    currentLanguage() {
      this.activeCategoryId = ''
      this.currentPage = 1
      this.categories = []
      this.categoriesLoaded = false
      this.refreshNews()
    }
  },
  methods: {
    async fetchNews(page) {
      const request = this.newsRequestGuard.start(this.currentLanguage)
      this.loading = true
      this.error = null

      try {
        const params = {
          pageIndex: page,
          pageSize: this.pageSize
        }
        if (this.activeCategoryId) params.categoryId = this.activeCategoryId

        const response = normalizeNewsPageResponse(await newsAPI.getNewsList(params, request.siteType))
        if (!this.newsRequestGuard.isCurrent(request, this.currentLanguage)) return

        this.articles = response.list
        this.totalCount = Number(response.total) || 0
        this.currentPage = Math.min(page, getTotalPages(this.totalCount, this.pageSize))
      } catch (requestError) {
        if (!this.newsRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('获取新闻数据失败:', requestError)
        this.error = this.t('news.networkError')
        this.articles = []
        this.totalCount = 0
      } finally {
        if (this.newsRequestGuard.isCurrent(request, this.currentLanguage)) this.loading = false
      }
    },
    async fetchCategories() {
      if (this.categoriesLoaded) return

      const request = this.categoryRequestGuard.start(this.currentLanguage)

      try {
        const categories = normalizeNewsCategoriesResponse(await newsAPI.getNewsCategories(request.siteType))
        if (!this.categoryRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.categories = categories
        this.categoriesLoaded = true
      } catch (requestError) {
        if (!this.categoryRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('获取新闻分类失败:', requestError)
      }
    },
    async selectCategory() {
      await this.fetchNews(1)
    },
    async changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage || this.loading) return
      await this.fetchNews(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async refreshNews() {
      await this.fetchNews(this.currentPage)
      await this.fetchCategories()
    }
  }
}
</script>

<style scoped>
.all-news-page {
  min-width: 0;
  min-height: 100vh;
  padding-top: var(--global-header-height, 44px);
  overflow: hidden;
  color: #000;
  background: #fff;
  font-family: MiSans, Arial, sans-serif;
}

.all-news-section {
  display: flex;
  flex-direction: column;
  padding: 80px 0 120px;
  box-sizing: border-box;
}

.all-news-container {
  width: min(calc(100vw - 144px), 1296px);
  display: flex;
  flex-direction: column;
  margin-right: auto;
  margin-left: max(72px, calc((100vw - 1296px) / 2));
}

.all-news-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.all-news-breadcrumb {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
}

.all-news-breadcrumb a {
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;
}

.all-news-breadcrumb strong {
  font-weight: 700;
}

.category-filter {
  position: relative;
}

.category-filter::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 13px;
  width: 6px;
  height: 6px;
  border-right: 1px solid #000;
  border-bottom: 1px solid #000;
  pointer-events: none;
  transform: translateY(-70%) rotate(45deg);
}

.category-filter select {
  width: 105px;
  min-width: 0;
  height: 28px;
  padding: 0 34px 0 11px;
  border: 0;
  border-radius: 46px;
  color: rgba(0, 0, 0, 0.5);
  background: #f5f5f7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: inherit;
  font-size: 12px;
  appearance: none;
  cursor: pointer;
}

.category-filter select:focus-visible,
.all-news-breadcrumb a:focus-visible,
.pagination-arrow:focus-visible,
.pagination-page:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.all-news-list {
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 22px;
}

.news-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 60px;
}

.pagination-arrow,
.pagination-page {
  width: 36px;
  height: 36px;
  display: inline-grid;
  flex: 0 0 36px;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  color: #000;
  background: #fff;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.pagination-arrow {
  border: 0;
}

.pagination-arrow img {
  width: 36px;
  height: 36px;
}

.pagination-arrow--previous img {
  transform: rotate(180deg);
}

.pagination-arrow:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.pagination-page:hover,
.pagination-page:focus-visible,
.pagination-page.is-active {
  border-color: #ef0a6a;
  color: #fff;
  background: #ef0a6a;
}

.pagination-ellipsis {
  width: 18px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
  text-align: center;
}

.all-news-state {
  min-height: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.all-news-state p {
  margin: 18px 0 0;
  color: rgba(0, 0, 0, 0.65);
}

.all-news-state--message {
  padding: 80px 16px;
}

.all-news-spinner {
  width: 34px;
  height: 34px;
  border: 2px solid rgba(0, 0, 0, 0.16);
  border-top-color: #ef0a6a;
  border-radius: 50%;
  animation: all-news-spin 0.8s linear infinite;
}

@keyframes all-news-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .all-news-container {
    width: calc(100vw - 48px);
    margin-left: 24px;
  }
}

@media (max-width: 700px) {
  .all-news-page {
    padding-top: var(--global-header-height, 50px);
  }

  .all-news-section {
    padding: 60px 0 64px;
  }

  .all-news-container {
    width: calc(100vw - 32px);
    margin-left: 16px;
  }

  .all-news-toolbar {
    align-items: flex-start;
    gap: 16px;
  }

  .all-news-list {
    margin-top: 31px;
  }

  .all-news-breadcrumb {
    flex-wrap: wrap;
    gap: 8px;
  }

  .all-news-list {
    gap: 48px;
    margin-top: 32px;
  }

  .news-pagination {
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 48px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .all-news-spinner {
    animation-duration: 1.6s;
  }
}
</style>
